import React from 'react';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { Pie } from '@visx/shape';
import type { HomeDashboard, HomeStatus } from '../../../api/homeV3Api';
import { statusLabels } from '../utils/formatters';
import styles from '../HomeV3.module.css';

interface Props {
    buckets: HomeDashboard['requestSummary']['statusBuckets'];
    selectedStatus: HomeStatus | null;
    onSelectStatus: (status: HomeStatus | null) => void;
}

const colors: Record<HomeStatus, string> = {
    open: '#4f6bed',
    inProgress: '#7668cb',
    completed: '#18794e',
    cancelled: '#9a5464',
    unknown: '#77798f',
};

export function RequestStatusDonut({ buckets, selectedStatus, onSelectStatus }: Props) {
    const nonEmpty = buckets.filter((bucket) => bucket.count > 0);
    const total = nonEmpty.reduce((sum, bucket) => sum + bucket.count, 0);
    if (total === 0) return <div className={styles.chartEmpty}>No current request workload.</div>;

    return (
        <div>
            <div className={styles.donutCanvas}>
                <ParentSize debounceTime={50}>{({ width, height }) => {
                    const radius = Math.max(10, Math.min(width, height) / 2 - 8);
                    return (
                        <svg width={width} height={height} role="img" aria-label={`Request status mix totaling ${total} requests`}>
                            <Group left={width / 2} top={height / 2}>
                                <Pie data={nonEmpty} pieValue={(bucket) => bucket.count} outerRadius={radius} innerRadius={radius * 0.62} padAngle={0.025}>
                                    {(pie) => pie.arcs.map((arc) => (
                                        <path key={arc.data.status} d={pie.path(arc) ?? undefined} fill={colors[arc.data.status]} opacity={selectedStatus && selectedStatus !== arc.data.status ? 0.35 : 1} />
                                    ))}
                                </Pie>
                                <text textAnchor="middle" dy="-0.1em" fill="currentColor" fontSize="24" fontWeight="700">{total}</text>
                                <text textAnchor="middle" dy="1.4em" fill="currentColor" fontSize="10">requests</text>
                            </Group>
                        </svg>
                    );
                }}</ParentSize>
            </div>
            <div className={styles.donutLegend} aria-label="Filter recent requests by status">
                {nonEmpty.map((bucket) => (
                    <button key={bucket.status} type="button" className={styles.legendButton} aria-pressed={selectedStatus === bucket.status} onClick={() => onSelectStatus(selectedStatus === bucket.status ? null : bucket.status)}>
                        <i style={{ background: colors[bucket.status] }} />{statusLabels[bucket.status]} <strong>{bucket.count}</strong>
                    </button>
                ))}
            </div>
        </div>
    );
}
